import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader, StatCard, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronLeft, Users, ListChecks, IndianRupee, Trophy, Crown, Printer } from "lucide-react";
import { istNow, useIstDay } from "../lib/dates";

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };

function presetRange(key) {
  const today = istNow();
  const s = (d) => fmt(d);
  if (key === "today") return [s(today), s(today)];
  if (key === "yesterday") { const y = new Date(today); y.setDate(y.getDate() - 1); return [s(y), s(y)]; }
  if (key === "week") return [s(startOfWeek(today)), s(today)];
  if (key === "month") return [s(new Date(today.getFullYear(), today.getMonth(), 1)), s(today)];
  if (key === "last_month") return [s(new Date(today.getFullYear(), today.getMonth() - 1, 1)), s(new Date(today.getFullYear(), today.getMonth(), 0))];
  return [s(new Date(today.getFullYear(), today.getMonth(), 1)), s(today)];
}

const PRESETS = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["month", "This Month"], ["last_month", "Last Month"], ["custom", "Custom"]];

export default function StaffServicePerformanceReport() {
  const navigate = useNavigate();
  const { branchParam } = useBranch();
  const [preset, setPreset] = useState("month");
  const [from, setFrom] = useState(presetRange("month")[0]);
  const [to, setTo] = useState(presetRange("month")[1]);
  const [staffId, setStaffId] = useState("all");
  const [serviceId, setServiceId] = useState("all");
  const [category, setCategory] = useState("all");
  const [staffList, setStaffList] = useState([]);
  const [services, setServices] = useState([]);
  const [data, setData] = useState({ staff: [], top_by_staff: [], grand: { staff_count: 0, total_services: 0, total_revenue: 0 } });
  const [sortBy, setSortBy] = useState("count"); // count | revenue
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/staff").then((r) => setStaffList(r.data || [])).catch(() => {});
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
  }, []);

  const categories = useMemo(() => [...new Set(services.map((s) => s.category).filter(Boolean))].sort(), [services]);

  // Re-rank each staff's services by the chosen metric (count = times performed, revenue = margin focus).
  const staffView = useMemo(() => (data.staff || []).map((st) => {
    const sorted = [...st.services].sort((a, b) => sortBy === "revenue"
      ? (b.revenue - a.revenue) || (b.count - a.count)
      : (b.count - a.count) || (b.revenue - a.revenue));
    return { ...st, services: sorted.map((s, i) => ({ ...s, rank: i + 1 })) };
  }), [data.staff, sortBy]);

  const topView = useMemo(() => staffView.map((st) => {
    const top = st.services[0];
    return { staff_id: st.staff_id, staff_name: st.staff_name,
             service_name: top?.service_name || "—", count: top?.count || 0, revenue: top?.revenue || 0 };
  }).sort((a, b) => sortBy === "revenue"
    ? (b.revenue - a.revenue) || (b.count - a.count)
    : (b.count - a.count) || (b.revenue - a.revenue)), [staffView, sortBy]);

  const metricLabel = sortBy === "revenue" ? "Highest Revenue Service" : "Most Performed Service";

  const setPresetRange = (key) => {
    setPreset(key);
    if (key !== "custom") { const [a, b] = presetRange(key); setFrom(a); setTo(b); }
  };
  const istDay = useIstDay();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (preset !== "custom") setPresetRange(preset); }, [istDay]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { from_date: from, to_date: to, branch_id: branchParam };
    if (staffId !== "all") params.staff_id = staffId;
    if (serviceId !== "all") params.service_id = serviceId;
    if (category !== "all") params.category = category;
    api.get("/reports/staff-service-performance", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ staff: [], top_by_staff: [], grand: { staff_count: 0, total_services: 0, total_revenue: 0 } }))
      .finally(() => setLoading(false));
  }, [from, to, branchParam, staffId, serviceId, category]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const head = ["Staff", "Service", "Times Performed", "Revenue", `Rank (by ${sortBy})`];
    const rows = [];
    staffView.forEach((st) => st.services.forEach((s) => rows.push([st.staff_name, s.service_name, s.count, s.revenue, s.rank])));
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `staff_service_performance_${from}_${to}.csv`; a.click();
  };

  return (
    <div data-testid="staff-service-performance-page">
      <Button variant="ghost" className="mb-2 text-muted-foreground" onClick={() => navigate("/reports")} data-testid="ssp-back"><ChevronLeft size={16} className="mr-1" /> Reports</Button>
      <PageHeader title="Staff Service Performance" subtitle="Which services each staff member performs the most, with revenue and rank"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={exportCsv} data-testid="ssp-export-csv">CSV</Button>
            <Button variant="outline" className="rounded-sm" onClick={() => window.print()} data-testid="ssp-print"><Printer size={15} className="mr-1" /> Print</Button>
          </div>
        } />

      <Card className="p-4 bg-card border-border mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex flex-wrap gap-2" data-testid="ssp-presets">
            {PRESETS.map(([k, label]) => (
              <Button key={k} size="sm" variant="ghost" onClick={() => setPresetRange(k)} data-testid={`ssp-preset-${k}`}
                className={`rounded-sm h-8 ${preset === k ? "bg-primary text-primary-foreground hover:bg-[#D4AF37]" : "bg-secondary"}`}>{label}</Button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-secondary rounded-sm p-1" data-testid="ssp-sort-toggle">
            <span className="text-xs text-muted-foreground px-2">Sort services by</span>
            <Button size="sm" variant="ghost" onClick={() => setSortBy("count")} data-testid="ssp-sort-count"
              className={`rounded-sm h-7 ${sortBy === "count" ? "bg-primary text-primary-foreground hover:bg-[#D4AF37]" : ""}`}>Times Performed</Button>
            <Button size="sm" variant="ghost" onClick={() => setSortBy("revenue")} data-testid="ssp-sort-revenue"
              className={`rounded-sm h-7 ${sortBy === "revenue" ? "bg-primary text-primary-foreground hover:bg-[#D4AF37]" : ""}`}>Revenue</Button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">From</label>
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} className="bg-secondary mt-1" data-testid="ssp-from" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">To</label>
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} className="bg-secondary mt-1" data-testid="ssp-to" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Staff</label>
            <Select value={staffId} onValueChange={setStaffId}>
              <SelectTrigger className="bg-secondary mt-1" data-testid="ssp-staff"><SelectValue placeholder="All Staff" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {staffList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Service</label>
            <Select value={serviceId} onValueChange={setServiceId}>
              <SelectTrigger className="bg-secondary mt-1" data-testid="ssp-service"><SelectValue placeholder="All Services" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-secondary mt-1" data-testid="ssp-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Users} label="Staff Covered" value={data.grand.staff_count} testid="ssp-kpi-staff" />
        <StatCard icon={ListChecks} label="Total Services Performed" value={data.grand.total_services} testid="ssp-kpi-services" />
        <StatCard icon={IndianRupee} label="Total Service Revenue" value={rupee(data.grand.total_revenue)} testid="ssp-kpi-revenue" />
      </div>

      {/* TOP SERVICE BY STAFF */}
      <h2 className="font-display text-xl mb-3 flex items-center gap-2"><Trophy size={18} className="text-primary" /> Top Service by Staff <span className="text-sm text-muted-foreground font-sans">· by {sortBy === "revenue" ? "revenue" : "times performed"}</span></h2>
      <Card className="bg-card border-border overflow-hidden mb-8">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead>Staff Name</TableHead>
              <TableHead>{metricLabel}</TableHead>
              <TableHead className="text-right">Count</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {topView.map((t) => (
              <TableRow key={t.staff_id} className="border-border" data-testid={`ssp-top-${t.staff_id}`}>
                <TableCell className="font-medium">{t.staff_name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="border-primary text-primary bg-primary/10 gap-1" data-testid={`ssp-top-service-${t.staff_id}`}>
                    <Crown size={12} /> {t.service_name}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">{t.count}×</TableCell>
                <TableCell className="text-right text-primary">{rupee(t.revenue)}</TableCell>
              </TableRow>
            ))}
            {!topView.length && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-10">{loading ? "Loading…" : "No completed service bills in this selection."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* PER-STAFF BREAKDOWN */}
      <h2 className="font-display text-xl mb-3">Per-Staff Breakdown <span className="text-sm text-muted-foreground font-sans">· ranked by {sortBy === "revenue" ? "revenue" : "times performed"}</span></h2>
      <div className="space-y-6">
        {staffView.map((st) => (
          <Card key={st.staff_id} className="bg-card border-border overflow-hidden" data-testid={`ssp-staff-${st.staff_id}`}>
            <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
              <p className="font-display text-lg">{st.staff_name}</p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-sm bg-secondary">Most performed: <span className="text-primary font-medium">{st.summary.most_performed_service}</span> ({st.summary.most_performed_count}×)</span>
                <span className="px-2 py-1 rounded-sm bg-secondary">Top revenue: <span className="text-green-400 font-medium">{st.summary.highest_revenue_service}</span> ({rupee(st.summary.highest_revenue)})</span>
                <span className="px-2 py-1 rounded-sm bg-secondary">Total: <span className="font-medium">{st.summary.total_services}</span> services · <span className="text-primary font-medium">{rupee(st.summary.total_revenue)}</span></span>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="w-16">Rank</TableHead>
                  <TableHead>Service Name</TableHead>
                  <TableHead className="text-right">Times Performed</TableHead>
                  <TableHead className="text-right">Revenue Generated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {st.services.map((s) => (
                  <TableRow key={`${st.staff_id}-${s.service_id || s.service_name}`} className={`border-border ${s.rank === 1 ? "bg-primary/5" : ""}`} data-testid={`ssp-row-${st.staff_id}-${s.rank}`}>
                    <TableCell>{s.rank === 1 ? <Badge variant="outline" className="border-primary text-primary gap-1"><Crown size={11} /> 1</Badge> : s.rank}</TableCell>
                    <TableCell className={s.rank === 1 ? "font-medium" : ""}>{s.service_name}<span className="block text-[11px] text-muted-foreground">{s.category}</span></TableCell>
                    <TableCell className="text-right">{s.count}×</TableCell>
                    <TableCell className="text-right text-primary">{rupee(s.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ))}
        {!staffView.length && <Card className="bg-card border-border p-10 text-center text-muted-foreground">{loading ? "Loading…" : "No completed service bills for this selection."}</Card>}
      </div>
    </div>
  );
}
